import { DateRange, RangePreset, RybbitScope, TimeBucket } from './types';

/** Presets the range picker offers, in the order it renders them. */
export const RANGE_PRESETS: ReadonlyArray<{
  value: RangePreset;
  label: string;
}> = [
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

export const DEFAULT_PRESET: RangePreset = '7d';

/** The browser's IANA zone — Rybbit requires one alongside a date range. */
export function browserTimeZone(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function rangeFor(
  preset: RangePreset,
  now: Date = new Date(),
): DateRange {
  const start = new Date(now);
  if (preset === '24h') start.setHours(start.getHours() - 24);
  else if (preset === '30d') start.setDate(start.getDate() - 30);
  else start.setDate(start.getDate() - 7);
  return { start, end: now };
}

/** `2026-08-29` in local time — Rybbit pairs these with an explicit time zone. */
function toLocalDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}`;
}

/**
 * Rybbit accepts one of three time-param sets; we always send the date one.
 * `time_zone` is required with it — without it the API 400s rather than
 * assuming UTC.
 */
export function timeParams(range: DateRange): Record<string, string> {
  return {
    start_date: toLocalDate(range.start),
    end_date: toLocalDate(range.end),
    time_zone: browserTimeZone(),
  };
}

/** Time window plus the entity's scoping filters, shared by every read. */
export function scopeParams(scope: RybbitScope): Record<string, string> {
  const params = timeParams(scope.range);
  if (scope.filters?.length) {
    params.filters = JSON.stringify(scope.filters);
  }
  return params;
}

/**
 * Bucket size for an error time series. A fixed bucket over a 30-day window
 * would render 43k points, and an hourly one over 24 h is what makes a spike
 * visible at all, so it follows the window.
 */
export function bucketFor(range: DateRange): TimeBucket {
  const hours = (range.end.getTime() - range.start.getTime()) / 3600_000;
  if (hours <= 48) return 'hour';
  return 'day';
}

/**
 * The window of the same length immediately before this one, for the
 * period-over-period figure on each tile.
 */
export function previousRange(range: DateRange): DateRange {
  const span = range.end.getTime() - range.start.getTime();
  return {
    start: new Date(range.start.getTime() - span),
    end: new Date(range.start),
  };
}

/**
 * Percent change, or undefined when there is no baseline to compare against —
 * anything over a zero previous period is an infinite rise, not a number.
 */
export function percentChange(
  current: number,
  previous: number,
): number | undefined {
  if (!previous) return undefined;
  return ((current - previous) / previous) * 100;
}
